/**
 * VRG-141 — capability badges (LOCKED rule, tag-technology-comparison.md Rev 5).
 *
 * Renders a capability as its ordered LEAF badges (one word each), never a
 * compound/"Sense" badge. Connect is shown on every tag (neutral). Family is
 * encoded by colour + icon: auth (Verify + Vault) = purple, Trace = own, Sense = own.
 */
import React from 'react'
import { Stack, Typography, Tooltip, useTheme, alpha } from '@mui/material'
import {
  NfcIcon, ShieldCheckIcon, KeyRoundIcon, ShieldAlertIcon, RadarIcon,
  ThermometerIcon, DropletsIcon, GaugeIcon, RadioIcon,
} from 'lucide-react'

import { palette } from 'constants/theme'
import {
  capabilityLeaves, LEAF_LABEL, LEAF_FAMILY, LEAF_BLURB, isLeafComingSoon,
  type CapabilityLeaf, type CapabilityFamily,
} from 'constants/tagCatalog'

import type { TagCapability } from 'constants/tagCatalog'
// Sibling path, not the `components` barrel: this file is exported FROM that barrel,
// so importing through it would be circular.
import { ComingSoonNote } from '../ComingSoonNote'

/** Appended to a coming-soon badge's tooltip. Short — the full reason lives on the
 *  product page's capability selector, where the customer is actually choosing. */
const COMING_SOON_BADGE_HINT = 'Not available to order yet — contact support to register interest.'

/**
 * `filled` — the family-coloured pill. Use where there is room to decode the colour and
 * where it does work: the product page's capability selector (one product, colour maps to
 * the chips beside it) and anywhere colour aids filter↔result matching.
 *
 * `plain` — icon + label only. Use in GRIDS. Across a card grid the badges are largely
 * identical (nearly every tag is Connect + Verify), so the colour is not discriminating
 * between cards — it is a wall of saturation that defeats scanning. Worse, the palette
 * encodes the auth/trace/sense FAMILY, a taxonomy the buyer has no key to; `leafColors`
 * says as much where it overrides Tamper to red because "the family grouping isn't
 * surfaced to users". Colour should encode difference, and on a card it encodes sameness.
 */
export type BadgeVariant = 'filled' | 'plain'

// Family → base colour (auth = purple; trace = blue; sense = orange; connect = neutral grey).
// Badge fills are derived as alpha tints of this base so they read on both light AND
// dark surfaces (the old solid [100]/[700] pairing washed out in dark mode).
export const FAMILY_BASE: Record<CapabilityFamily, { base: string; ramp: Record<number, string> }> = {
  connect: { base: palette.gray[500], ramp: palette.gray },
  auth: { base: palette.purple[500], ramp: palette.purple },
  trace: { base: palette.blue[500], ramp: palette.blue },
  sense: { base: palette.orange[500], ramp: palette.orange },
}

/**
 * Resolve a capability family to the EXACT fill / border / text stops the badge uses,
 * so other surfaces (e.g. the product-page capability chips, VRG-156) colour by family
 * identically to the cards — one source of truth, no re-hardcoded hexes.
 */
export function familyColors(family: CapabilityFamily, dark: boolean) {
  const { base, ramp } = FAMILY_BASE[family]
  return {
    base,
    fill: alpha(base, dark ? 0.22 : 0.1),
    border: alpha(base, dark ? 0.45 : 0.32),
    // light-tint text stop: [200] in dark, [700] in light — reads on the alpha fill.
    text: dark ? ramp[200] : ramp[700],
  }
}

/**
 * Per-LEAF badge colours. Same as the leaf's family colours, EXCEPT Tamper is shown
 * RED — to buyers it reads as a security / tamper-evidence capability, so red fits it
 * better than the Sense orange (the family grouping isn't surfaced to users). One
 * source so every surface (badges, product-page atom chips, filter chips) agrees.
 */
export function leafColors(leaf: CapabilityLeaf, dark: boolean) {
  if (leaf === 'tamper') {
    const ramp = palette.red
    const base = ramp[500]
    return {
      base,
      fill: alpha(base, dark ? 0.22 : 0.1),
      border: alpha(base, dark ? 0.45 : 0.32),
      text: dark ? ramp[200] : ramp[700],
    }
  }
  return familyColors(LEAF_FAMILY[leaf], dark)
}

const LEAF_ICON: Record<CapabilityLeaf, React.ComponentType<{ size?: number; strokeWidth?: number }>> = {
  connect: NfcIcon,
  verify: ShieldCheckIcon,
  vault: KeyRoundIcon,
  tamper: ShieldAlertIcon,
  trace: RadarIcon,
  temperature: ThermometerIcon,
  moisture: DropletsIcon,
  pressure: GaugeIcon,
  proximity: RadioIcon,
}

function LeafBadge({
  leaf,
  size = 'md',
  variant = 'filled',
}: {
  leaf: CapabilityLeaf
  size?: 'sm' | 'md'
  variant?: BadgeVariant
}) {
  const theme = useTheme()
  const dark = theme.palette.mode === 'dark'
  const { fill, border, text } = leafColors(leaf, dark)
  const Icon = LEAF_ICON[leaf]
  const iconSize = size === 'sm' ? 14 : 16

  // A capability we can't fulfil yet is shown, but drained of its family colour so it
  // reads as unavailable at a glance rather than as one more thing you can buy. The
  // label carries the fact in words too -- colour alone is not an accessible signal.
  const comingSoon = isLeafComingSoon(leaf)
  const mutedBase = dark ? palette.gray[400] : palette.gray[500]

  // ── plain: icon + label, no fill, no border ────────────────────────────────────
  // Matches CategoryCell's `filter` mode, which is what the "Best for" row on the same
  // card already uses — so Capabilities stops being the one field rendering as saturated
  // pills while Sizes, Range and Best for are quiet text.
  //
  // Fully neutral, including Tamper, which keeps its red only in `filled`. On a card the
  // point is to stop colour competing; the tooltip still carries the meaning, and the
  // coming-soon state still says "· Soon" in words rather than relying on a colour drain.
  if (variant === 'plain') {
    return (
      <Tooltip
        title={
          comingSoon ? (
            <ComingSoonNote>{`${LEAF_BLURB[leaf]} ${COMING_SOON_BADGE_HINT}`}</ComingSoonNote>
          ) : (
            LEAF_BLURB[leaf]
          )
        }
        placement="bottom"
        arrow
      >
        <Stack
          direction="row"
          spacing={1.25}
          alignItems="center"
          sx={{
            color: comingSoon ? 'text.secondary' : 'text.primary',
            maxWidth: 'fit-content',
            cursor: 'default',
          }}
        >
          <Icon size={iconSize} strokeWidth={1} />
          <Typography variant="body3" sx={{ lineHeight: 1 }} noWrap>
            {LEAF_LABEL[leaf]}
          </Typography>
          {comingSoon && (
            <Typography
              variant="body3"
              color="text.secondary"
              sx={{ lineHeight: 1, opacity: 0.8, whiteSpace: 'nowrap' }}
            >
              · Soon
            </Typography>
          )}
        </Stack>
      </Tooltip>
    )
  }

  return (
    // Hover a badge to learn what the capability does (cards, product page, filter).
    <Tooltip
      title={
        comingSoon ? (
          <ComingSoonNote>{`${LEAF_BLURB[leaf]} ${COMING_SOON_BADGE_HINT}`}</ComingSoonNote>
        ) : (
          LEAF_BLURB[leaf]
        )
      }
      placement="bottom"
      arrow
    >
      <Stack
        direction="row"
        spacing={1.25}
        alignItems="center"
        sx={{
          px: size === 'sm' ? 2 : 2.5,
          // md py:2 makes the badge 34px tall — matching the Size/Quantity chipSx chips on
          // the product page (its only md usage), so all three rows line up. sm unchanged.
          py: size === 'sm' ? 0.75 : 2,
          borderRadius: 2,
          color: comingSoon ? mutedBase : text,
          backgroundColor: comingSoon ? alpha(mutedBase, dark ? 0.14 : 0.08) : fill,
          border: `1px dashed ${comingSoon ? alpha(mutedBase, 0.4) : border}`,
          ...(comingSoon ? null : { borderStyle: 'solid' }),
          maxWidth: 'fit-content',
          cursor: 'default',
        }}
      >
        <Icon size={iconSize} strokeWidth={1.5} />
        <Typography variant={size === 'sm' ? 'body3' : 'body2'} sx={{ fontWeight: 500, lineHeight: 1 }}>
          {LEAF_LABEL[leaf]}
        </Typography>
        {comingSoon && (
          <Typography
            variant="body3"
            sx={{ fontWeight: 500, lineHeight: 1, opacity: 0.8, whiteSpace: 'nowrap' }}
          >
            · Soon
          </Typography>
        )}
      </Stack>
    </Tooltip>
  )
}

// Canonical badge order: Connect (baseline) → auth (Verify, Vault) → Trace → Sense
// (Tamper, Temperature, Moisture, Pressure). Fixed so every badge row reads the same.
const LEAF_ORDER: CapabilityLeaf[] = [
  'connect', 'verify', 'vault', 'trace', 'tamper', 'temperature', 'moisture', 'pressure', 'proximity',
]

export const TagCapabilityBadges = (props: {
  /** one capability, or a set (e.g. a form offering Connect/Verify/Tamper → the union of leaves). */
  capability?: TagCapability | TagCapability[]
  /** render EXACTLY these leaf badges (e.g. the guide's one-badge-per-row). Overrides `capability`. */
  leaves?: CapabilityLeaf[]
  size?: 'sm' | 'md'
  /** hide the always-present Connect badge (e.g. dense list rows). Default shows it. */
  hideConnect?: boolean
  /** `plain` for grids (see BadgeVariant); `filled` elsewhere. Default `filled`. */
  variant?: BadgeVariant
}) => {
  const { capability, leaves: explicitLeaves, size = 'md', hideConnect = false, variant = 'filled' } = props
  const caps = capability ? (Array.isArray(capability) ? capability : [capability]) : []
  const union = explicitLeaves
    ? new Set<CapabilityLeaf>(explicitLeaves)
    : new Set<CapabilityLeaf>(caps.flatMap(capabilityLeaves))
  const leaves = LEAF_ORDER.filter(l => union.has(l) && !(hideConnect && l === 'connect'))
  return (
    // Plain items carry no pill boundary, so they need a wider gap to read as separate
    // entries rather than one run-on phrase; filled pills supply their own edge.
    <Stack
      direction="row"
      spacing={variant === 'plain' ? 4 : 1.5}
      alignItems="center"
      flexWrap="wrap"
      useFlexGap
    >
      {leaves.map(leaf => (
        <LeafBadge key={leaf} leaf={leaf} size={size} variant={variant} />
      ))}
    </Stack>
  )
}
